/**
 * Users admin page (F6a — split from the old combined Users & Groups
 * page). Lists users with inline "API keys" expansion + the create/
 * edit dialog. Groups live in a separate sidebar entry now
 * (``GroupsPage`` at ``/groups``).
 *
 * The per-user "what groups is this user in" view stays inside
 * UserDialog (membership picker on the edit form) — this page is
 * the operator's catalog of users, not a group editor.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut, Mail, Pencil, Plus, ShieldCheck, ShieldOff, Users } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { ApiKeysPanel } from "@/components/users/ApiKeysPanel";
import { InviteUserDialog } from "@/components/users/InviteUserDialog";
import { SetPasswordDialog } from "@/components/users/SetPasswordDialog";
import { UserDialog, type UserDialogMode } from "@/components/users/UserDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { usersApi } from "@/lib/api-users";
import { usePaginationState } from "@/lib/pagination";
import { fetchTenantContext } from "@/lib/platform";

export function UsersPage() {
  const { t } = useTranslation();
  const [dialog, setDialog] = React.useState<{ open: boolean; mode: UserDialogMode }>({
    open: false,
    mode: { kind: "create" },
  });
  const [expanded, setExpanded] = React.useState<string | null>(null);
  // Target of the "set password" dialog (operator escape hatch).
  const [pwTarget, setPwTarget] = React.useState<{ id: string; username: string } | null>(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);

  // The central-door affordances (invite + local-door toggle, ADR-0077 S3) exist
  // ONLY where the Account Tray login does — a subdomain-resolved SaaS tenant.
  // On-prem/apex the probe carries no central_login_url, so the page stays
  // exactly as before by construction.
  const [hasCentralDoor, setHasCentralDoor] = React.useState(false);
  React.useEffect(() => {
    void fetchTenantContext().then((ctx) => setHasCentralDoor(Boolean(ctx.central_login_url)));
  }, []);

  const qc = useQueryClient();
  const usersPage = usePaginationState();
  const users = useQuery({
    queryKey: ["users", usersPage.page, usersPage.pageSize],
    queryFn: () =>
      usersApi.list({ page: usersPage.page, page_size: usersPage.pageSize }),
  });
  const reset2fa = useMutation({
    mutationFn: (id: string) => usersApi.resetTwoFactor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  const revokeSessions = useMutation({
    mutationFn: (id: string) => usersApi.revokeSessions(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
  // Operator 2FA enforcement policy (ADR-0017) — lives here, not the portal
  // Security page, so it's governed by the `users` RBAC.
  const secQ = useQuery({ queryKey: ["operator-security"], queryFn: () => usersApi.getSecurity() });
  // PUT carries BOTH flags (the backend merges into the shared security blob),
  // so each toggle patches its own field over the current server copy.
  const saveSec = useMutation({
    mutationFn: (patch: Partial<{ totp_required_users: boolean; local_accounts_enabled: boolean }>) =>
      usersApi.updateSecurity({
        totp_required_users: secQ.data?.totp_required_users ?? false,
        local_accounts_enabled: secQ.data?.local_accounts_enabled ?? true,
        ...patch,
      }),
    onSuccess: (saved) => qc.setQueryData(["operator-security"], saved),
  });

  const userItems = users.data?.items ?? [];
  const userTotal = users.data?.total ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title={t("users.title")}
        description={t("users.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            {hasCentralDoor && (
              <Button size="sm" onClick={() => setInviteOpen(true)}>
                <Mail className="h-4 w-4" strokeWidth={1.5} />
                {t("users.invite", "Invita")}
              </Button>
            )}
            <Button
              size="sm"
              variant={hasCentralDoor ? "outline" : "default"}
              onClick={() => setDialog({ open: true, mode: { kind: "create" } })}
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("users.newUser")}
            </Button>
          </div>
        }
      />
      <PageContent>
        {/* Operator 2FA enforcement (ADR-0017). */}
        <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-surface px-4 py-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <div>
              <div className="text-sm font-medium text-foreground">
                {t("twoFactor.requireOperators")}
              </div>
              <div className="text-xs text-muted-foreground">{t("twoFactor.policyHelp")}</div>
            </div>
          </div>
          <Switch
            checked={secQ.data?.totp_required_users ?? false}
            disabled={secQ.isPending || saveSec.isPending}
            onCheckedChange={(v) => saveSec.mutate({ totp_required_users: v })}
          />
        </div>
        {/* The local-login door (ADR-0077 S3) — only where the central door exists,
            so it can never lock an on-prem workspace out of its only login. */}
        {hasCentralDoor && (
          <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-surface px-4 py-3">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <div>
                <div className="text-sm font-medium text-foreground">
                  {t("users.localDoor", "Consenti accessi con account locali")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t(
                    "users.localDoorHelp",
                    "Spento, la pagina di accesso mostra solo Account Tray. Spegnilo solo quando ogni operatore è centrale.",
                  )}
                </div>
              </div>
            </div>
            <Switch
              checked={secQ.data?.local_accounts_enabled ?? true}
              disabled={secQ.isPending || saveSec.isPending}
              onCheckedChange={(v) => saveSec.mutate({ local_accounts_enabled: v })}
            />
          </div>
        )}
        {userItems.length > 0 ? (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Username</Th>
                  <Th>Email</Th>
                  <Th>Organization</Th>
                  <Th>Groups</Th>
                  <Th>State</Th>
                  <Th>Last login</Th>
                  <Th className="text-right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {userItems.map((u) => (
                  <React.Fragment key={u.user_id}>
                    <Tr>
                      <Td className="font-medium">
                        <div className="flex items-center gap-2">
                          {u.username}
                          {u.identity_id && (
                            <Badge tone="active">{t("users.centralBadge", "Account Tray")}</Badge>
                          )}
                        </div>
                      </Td>
                      <Td className="text-muted-foreground">{u.email}</Td>
                      <Td className="text-muted-foreground">{u.organization ?? "—"}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {u.groups.map((g) => (
                            <Badge key={g}>{g}</Badge>
                          ))}
                        </div>
                      </Td>
                      <Td>
                        <Badge tone={u.state === "active" ? "active" : "deprecated"}>
                          {u.state}
                        </Badge>
                      </Td>
                      <Td className="text-muted-foreground text-xs">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                      </Td>
                      <Td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setExpanded((curr) => (curr === u.user_id ? null : u.user_id))
                            }
                          >
                            {expanded === u.user_id ? "Hide" : "API keys"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t("users.revokeSessions")}
                            title={t("users.revokeSessions")}
                            onClick={() => {
                              if (window.confirm(t("users.revokeSessionsConfirm", { username: u.username })))
                                revokeSessions.mutate(u.user_id);
                            }}
                          >
                            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                          {/* A central member has no local password to reset — their
                              credential lives on the Account Tray (backend 422s too). */}
                          {!u.identity_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t("users.setPassword")}
                              title={t("users.setPassword")}
                              onClick={() => setPwTarget({ id: u.user_id, username: u.username })}
                            >
                              <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
                            </Button>
                          )}
                          {u.totp_enabled && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={t("twoFactor.reset")}
                              onClick={() => {
                                if (window.confirm(t("twoFactor.resetConfirm")))
                                  reset2fa.mutate(u.user_id);
                              }}
                            >
                              <ShieldOff className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${u.username}`}
                            onClick={() =>
                              setDialog({ open: true, mode: { kind: "edit", user: u } })
                            }
                          >
                            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                    {expanded === u.user_id && (
                      <Tr>
                        <Td colSpan={7} className="bg-muted/30 p-4">
                          <ApiKeysPanel userId={u.user_id} />
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
            <Pagination
              page={usersPage.page}
              pageSize={usersPage.pageSize}
              total={userTotal}
              onPageChange={usersPage.setPage}
              onPageSizeChange={usersPage.setPageSize}
            />
          </div>
        ) : (
          <EmptyState icon={Users} title={t("users.noUsers")} />
        )}
      </PageContent>

      <UserDialog
        open={dialog.open}
        mode={dialog.mode}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
      />
      <SetPasswordDialog target={pwTarget} onClose={() => setPwTarget(null)} />
      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </PageContainer>
  );
}
